/**
 * Allowlisted Sprout stream overrides. The member stream ignores any other
 * `model` / `reasoningEffort` values from the client.
 *
 * Keep this list aligned with the models Sprout already runs in production
 * (primary gpt-5.6-sol, then gpt-5.4 and gpt-5.5) and each model's OpenAI
 * `reasoning.effort` support.
 */
export const SPROUT_STREAM_MODEL_IDS = [
    'gpt-5.6-sol',
    'gpt-5.4',
    'gpt-5.5',
] as const;

export type SproutStreamModelId = (typeof SPROUT_STREAM_MODEL_IDS)[number];

export const DEFAULT_SPROUT_STREAM_MODEL_ID =
    'gpt-5.6-sol' satisfies SproutStreamModelId;

export const SPROUT_STREAM_REASONING_EFFORTS = [
    'none',
    'low',
    'medium',
    'high',
    'xhigh',
] as const;

export type SproutStreamReasoningEffort =
    (typeof SPROUT_STREAM_REASONING_EFFORTS)[number];

export const DEFAULT_SPROUT_STREAM_REASONING_EFFORT =
    'low' satisfies SproutStreamReasoningEffort;

const SPROUT_STREAM_MODEL_ID_SET = new Set<string>(SPROUT_STREAM_MODEL_IDS);
const SPROUT_STREAM_REASONING_EFFORT_SET = new Set<string>(
    SPROUT_STREAM_REASONING_EFFORTS,
);

export const isSproutStreamModelId = (
    value: unknown,
): value is SproutStreamModelId =>
    typeof value === 'string' && SPROUT_STREAM_MODEL_ID_SET.has(value);

export const isSproutStreamReasoningEffort = (
    value: unknown,
): value is SproutStreamReasoningEffort =>
    typeof value === 'string' && SPROUT_STREAM_REASONING_EFFORT_SET.has(value);

export const getSproutStreamReasoningEffortsForModel = (
    modelId: SproutStreamModelId,
): readonly SproutStreamReasoningEffort[] => {
    switch (modelId) {
        case 'gpt-5.6-sol':
        case 'gpt-5.5':
        case 'gpt-5.4':
            return ['none', 'low', 'medium', 'high', 'xhigh'];
        default: {
            const exhaustive: never = modelId;
            return exhaustive;
        }
    }
};

export const isSproutStreamReasoningEffortForModel = (
    modelId: SproutStreamModelId,
    value: unknown,
): value is SproutStreamReasoningEffort =>
    getSproutStreamReasoningEffortsForModel(modelId).some(
        (effort) => effort === value,
    );

export const parseSproutStreamOverrides = ({
    model,
    reasoningEffort,
}: {
    model?: unknown;
    reasoningEffort?: unknown;
}): {
    model?: SproutStreamModelId;
    reasoningEffort?: SproutStreamReasoningEffort;
} => ({
    ...(isSproutStreamModelId(model) ? { model } : {}),
    ...(isSproutStreamReasoningEffort(reasoningEffort)
        ? { reasoningEffort }
        : {}),
});

/**
 * Coerce a model / effort pair into one OpenAI will accept. Unknown models
 * become the Sprout default; an effort that the model does not support
 * becomes `low` when that is allowed, otherwise the model's first effort.
 */
export const clampSproutStreamSelection = ({
    modelId,
    reasoningEffort,
}: {
    modelId?: unknown;
    reasoningEffort?: unknown;
}): {
    modelId: SproutStreamModelId;
    reasoningEffort: SproutStreamReasoningEffort;
} => {
    const model = isSproutStreamModelId(modelId)
        ? modelId
        : DEFAULT_SPROUT_STREAM_MODEL_ID;

    if (isSproutStreamReasoningEffortForModel(model, reasoningEffort)) {
        return { modelId: model, reasoningEffort };
    }

    const efforts = getSproutStreamReasoningEffortsForModel(model);
    const defaultEffort = efforts.find(
        (effort) => effort === DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
    );

    return {
        modelId: model,
        reasoningEffort:
            defaultEffort ??
            efforts[0] ??
            DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
    };
};
